/**
 * Maps a *resolved* (HTTP 200) regenerate response's terminal
 * incomplete/failure reason to a safe, user-friendly message.
 *
 * The backend can legitimately be unable to find a compliant plan for a
 * day's open slots and still return HTTP 200 with success:false — this is
 * distinct from a thrown request error (see friendlyPlanError in
 * PlanReview.jsx, which handles that case). Before this existed,
 * PlanReview's handleRegenerateDay only handled `success === true`, so this
 * legitimate terminal state fell through with no toast and no state change —
 * the spinner just stopped, indistinguishable from a broken/no-op button
 * (see AGENTS.md pre-launch audit, finding LEFT-1).
 *
 * Exported as a plain map (not just the single-reason function below) so
 * other regeneration surfaces — e.g. PlanningHub's /smart-fill/auto result
 * classifier, which resolves a reason from a per-date `daily_results[i]`
 * entry rather than a single top-level `data.reason` — can reuse the exact
 * same reason -> copy mapping instead of maintaining a second one.
 */
export var REGENERATE_FAILURE_REASON_MESSAGES = {
  search_failed: "Couldn't find a new plan for this day that fits your remaining targets. Try removing a filter or excluded meal, then regenerate again.",
  catalog_infeasible: "There aren't enough different meals left to regenerate this day without repeating one you've already excluded. Try clearing some exclusions.",
  generation_failed: "Couldn't generate a new plan for this day. Please try again.",
};

export function friendlyRegenerateDayFailure(data, fallback) {
  var reason = (data && (data.reason || data.error)) || null;
  return friendlyRegenerateReasonText(reason, fallback);
}

/** Same reason -> copy mapping as friendlyRegenerateDayFailure, but taking
 * an already-resolved reason string directly (for callers whose reason
 * doesn't come from a single top-level data.reason/data.error field). */
export function friendlyRegenerateReasonText(reason, fallback) {
  if (reason && REGENERATE_FAILURE_REASON_MESSAGES[reason]) {
    return REGENERATE_FAILURE_REASON_MESSAGES[reason];
  }
  return fallback;
}
